import $ from 'jquery';
import * as _ from 'lodash';
import { Cesium } from 'app/cesium';

class CursorPosition {
    x;
    y;

    constructor(mouseEvent) {
        this.x = mouseEvent.pageX;
        this.y = mouseEvent.pageY;
    }

    comparePositions(other2DPosition) {
        return {
            x: this.x - other2DPosition.x,
            y: this.y - other2DPosition.y,
        };
    }
}

export class Compass {
    _calculateAngleAroundZeroZero: (x: number, y: number) => number;
    _viewer;
    _lastPosition;
    _listeners;
    _northHeadingDegrees;
    _previousAngleCalculation;

    constructor(calculateAngleAroundZeroZero) {
        this._viewer = null;
        this._lastPosition = null;
        this._listeners = [];
        this._northHeadingDegrees = 0;
        this._previousAngleCalculation = 0;
        this._calculateAngleAroundZeroZero = calculateAngleAroundZeroZero;
    }

    getCompassRingElement() {
        return $('#compass-ring');
    }

    onChange(listener) {
        this._listeners.push(listener);
        return () => {
            _.remove(this._listeners, (l) => {
                return l === listener;
            });
        };
    }

    get northHeadingDegrees() {
        return this._northHeadingDegrees;
    }

    _updateNorthHeading() {
        this._northHeadingDegrees = -this._viewer.camera.heading * 57.2958;

        _.forEach(this._listeners, (listener) => {
            listener(this);
        });
    }

    initialise(viewer) {
        this._viewer = viewer;

        // Increase the rate change events are run (catches small changes)
        this._viewer.camera.percentageChanged = 0.1;

        // Add an event listener that updates our compasses north heading.
        this._viewer.camera.changed.addEventListener(() => {
            this._updateNorthHeading();
        });
        this._viewer.camera.moveStart.addEventListener(() => {
            this._updateNorthHeading();
        });
        this._viewer.camera.moveEnd.addEventListener(() => {
            this._updateNorthHeading();
        });
        this._updateNorthHeading();
    }

    startDrag(mouseEvent) {
        this._lastPosition = new CursorPosition(mouseEvent);

        this._previousAngleCalculation = this._calculateCursorsAngleToRing(this._lastPosition);
    }

    stopDrag() {
        this._lastPosition = null;

        this._previousAngleCalculation = 0;
    }

    dragging(mouseEvent, id) {
        //Rotates the camera on it's pivot
        const currentPosition = new CursorPosition(mouseEvent);

        this._updateCameraRotation(currentPosition);

        this._lastPosition = currentPosition;
    }

    _calculateCursorsAngleToRing(cursorPosition) {
        let offset: any = this.getCompassRingElement().offset();

        offset.x = offset.left + this.getCompassRingElement().width() / 2;
        offset.y = offset.top + this.getCompassRingElement().height() / 2;

        // Calculate our relative x/y to the center of the compass.
        const relativePosition = cursorPosition.comparePositions(offset);

        // Determine our angle
        return this._calculateAngleAroundZeroZero(relativePosition.x, relativePosition.y);
    }

    _updateCameraRotation(cursorPosition) {
        const camera = this._viewer.camera;
        const scene = this._viewer.scene;

        // Determine the center of the viewport
        const windowPosition = new Cesium.Cartesian2();
        windowPosition.x = scene.canvas.clientWidth / 2;
        windowPosition.y = scene.canvas.clientHeight / 2;

        // Determine the cartesian3 to rotate around
        const ray = camera.getPickRay(windowPosition);
        const viewCenterCart3 = scene.globe.pick(ray, scene);

        // Determine the angle between our cursor position and the center of our compass ring
        const calculatedAngle = this._calculateCursorsAngleToRing(cursorPosition);

        // The angle(in radians) to rotate by
        const angle = calculatedAngle - this._previousAngleCalculation;

        // Rotate the camera
        if (viewCenterCart3) {
            camera.rotate(viewCenterCart3, -angle);
        }

        // Set our new angle
        this._previousAngleCalculation = calculatedAngle;

        // Update our compasses north
        this._updateNorthHeading();
    }
}
